import { Link } from "react-router-dom";
import { ArrowLeft, Info } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  LearnerFormEditor,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError, type LearnerFormUpdateRequest } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";
import { useTeamForm, useUpdateTeamForm } from "@/hooks/useTeamForm";
import { useTerms } from "@/hooks/useTerms";

/**
 * Phase SC-22 — the institution's team invite form.
 *
 * Read-only unless the platform has handed editing over and the viewer
 * is an admin or owner. `PUT /me/team-form` enforces the same rule.
 */
export function TeamFormSettingsPage() {
  const { user } = useAuth();
  const { terms } = useTerms();
  const formQuery = useTeamForm();
  const save = useUpdateTeamForm();

  const personOne = terms.team.one.toLowerCase();
  const title = "Team form";
  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const form = formQuery.data;
  const canEdit = Boolean(form?.editable_by_tenant) && isAdmin;

  async function handleSave(body: LearnerFormUpdateRequest) {
    try {
      await save.mutateAsync(body);
      toast.success("Team form saved.");
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 403
          ? "Editing the team form was turned off. Refresh to see the latest version."
          : err instanceof ApiError && err.status === 422
            ? "Some fields need another look. Check the names and choices, then save again."
            : "Couldn't save the team form. Try again in a moment.";
      toast.error(message);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 pb-12 lg:p-8">
      <PageHeader>
        <PageHeaderTop>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ArrowLeft className="size-4" aria-hidden="true" />
              Settings
            </Link>
          </Button>
        </PageHeaderTop>
        <PageHeaderHeading>
          <PageHeaderTitle>{title}</PageHeaderTitle>
          <PageHeaderDescription>
            The details your team fills in when inviting a {personOne}, in the order they appear.
          </PageHeaderDescription>
        </PageHeaderHeading>
      </PageHeader>

      {formQuery.isLoading ? (
        <Card aria-busy="true">
          <CardContent className="grid gap-2 py-6">
            {Array.from({ length: 5 }, (_, i) => (
              <div
                key={i}
                className="bg-muted h-10 animate-pulse rounded-md motion-reduce:animate-none"
              />
            ))}
          </CardContent>
        </Card>
      ) : formQuery.isError || !form ? (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-6 text-sm">
            <p className="text-destructive">Couldn&apos;t load the team form.</p>
            <Button variant="outline" size="sm" onClick={() => void formQuery.refetch()}>
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Fields</CardTitle>
            <CardDescription>
              {canEdit
                ? "Rename, reorder, hide or require fields, and add extra ones you need."
                : "Fields marked Required must be filled in before an invite can be sent."}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            {!canEdit ? (
              <div
                role="note"
                className="bg-muted/40 text-muted-foreground flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm"
              >
                <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <p>
                  {form.editable_by_tenant
                    ? "Only an admin can change the team form."
                    : "This form is set up for you by EduViaOS. To add or change a field, message support and we'll update it."}
                </p>
              </div>
            ) : null}
            <LearnerFormEditor
              form={form}
              readOnly={!canEdit}
              saving={save.isPending}
              onSave={(body) => void handleSave(body)}
              personNoun={personOne}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
